import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { ModuleAccessId } from '@connexio/shared';
import { SHOWCASE_SECTIONS } from '@/features/showcase/registry';
import { ContactEmailNote } from './ContactEmailNote';
import { PrimaryCta } from './PrimaryCta';
import { Section, SectionHeading } from './Section';
import styles from './ValueProps.module.css';

/** Outcome first (the key), the module that delivers it second (its icon). */
const ITEMS: { key: string; module: ModuleAccessId }[] = [
  { key: 'deals', module: 'deals' },
  { key: 'followUp', module: 'tasks' },
  { key: 'customers', module: 'contacts' },
  { key: 'quotes', module: 'quotes' },
  { key: 'paid', module: 'invoices' },
  { key: 'lookup', module: 'companies' },
];

interface ValuePropsProps {
  signupEnabled: boolean;
}

export function ValueProps({ signupEnabled }: ValuePropsProps) {
  const { t } = useTranslation('marketing');

  return (
    <Section id="product" tone="light" labelledBy="value-title">
      <SectionHeading
        id="value-title"
        eyebrow={t('value.eyebrow')}
        title={t('value.title')}
        description={t('value.description')}
        align="center"
      />

      <ul className={styles.list}>
        {ITEMS.map(({ key, module }) => {
          const Icon = SHOWCASE_SECTIONS[module].icon;
          return (
            <li
              key={key}
              className={styles.item}
            >
              <span className={styles.text}>
                <Icon className={styles.icon} aria-hidden="true" />
              </span>
              <h3 className={styles.heading}>
                {t(`value.items.${key}.title`)}
              </h3>
              <p className={styles.text2}>
                {t(`value.items.${key}.body`)}
              </p>
              <p className={styles.text3}>
                {t(`value.items.${key}.feature`)}
              </p>
            </li>
          );
        })}
      </ul>

      <div className={styles.stack}>
        <PrimaryCta signupEnabled={signupEnabled} />
        <Button asChild variant="outline" size="xl">
          <Link to="/product">
            {t('value.seeProduct')}
            <ArrowRight className={styles.icon2} aria-hidden="true" />
          </Link>
        </Button>
      </div>
      {signupEnabled ? null : <ContactEmailNote className={styles.contactEmailNote} />}
    </Section>
  );
}
